import { buddyClient, qs } from './client';

/**
 * Knowledge (H5): skills, the documentation index and customer documents. Types follow buddy3's
 * `packages/contracts/src/{skills,docs,customerDocs}.ts` (docs/contracts.md "Skills", "Documentation index", "Customer
 * documents", ADR-0017), trimmed to what the hub shows.
 */

// ------------------------------------------------------------------------------------------------ skills

/** Where a skill row sits in the cascade; the most specific level with a row for a `key` decides. */
export type SkillScope = 'global' | 'tenant' | 'organization' | 'system';
export type ToggleScope = Exclude<SkillScope, 'global'>;

/** `system` ships with the product (read-only here; a scope may only switch it off), `custom` is written here. */
export type SkillOrigin = 'system' | 'custom';

export interface Skill {
  id: string;
  origin: SkillOrigin;
  /** What the model types into load_skill; a chapter's key is `<parent>/<name>`. */
  key: string;
  /** '' for a skill of its own; a chapter stays out of the prompt's index. */
  parentKey: string;
  scope: SkillScope;
  /** Tenant, organization or system id; '' for a global skill. */
  scopeId: string;
  name: string;
  /** The "use this when…" line: the only part always in the prompt. */
  description: string;
  /** Markdown sent verbatim; `null` on an override row = inherit the wider level's text. */
  body: string | null;
  always: boolean;
  enabled: boolean;
  sortOrder: number;
  edited?: boolean;
  /** A registered validation rule, or ''. */
  validator: string;
  /** `onFail: 'repair' | 'warn'`. */
  validatorConfig?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export type SkillInput = Omit<Skill, 'id' | 'origin' | 'edited' | 'createdAt' | 'updatedAt'>;

export interface EffectiveSkills {
  systemId: string;
  /** False when the `skills` feature flag is off for the system, whatever rows exist. */
  featureEnabled: boolean;
  skills: Skill[];
}

export interface SkillValidator {
  name: string;
  description: string;
}

/** `errored` never blocks: it is how a silently broken rule stays visible. */
export type ValidationOutcome = 'passed' | 'repaired' | 'warned' | 'errored';

export interface ValidationRun {
  id: number;
  createdAt: string;
  systemId: string;
  userCode: string;
  conversationId?: string | null;
  messageId?: string | null;
  skillKey: string;
  validator: string;
  outcome: ValidationOutcome;
  checked: number;
  unverified: number;
  failed: number;
  gated: boolean;
  findings: { artifact: string; problem: string; detail?: string }[];
  durationMs: number;
}

export interface ValidationStats {
  skillKey: string;
  validator: string;
  total: number;
  passed: number;
  repaired: number;
  warned: number;
  errored: number;
  lastAt?: string | null;
}

export interface Validations {
  stats: ValidationStats[];
  runs: ValidationRun[];
}

export interface ValidationQuery {
  skill?: string;
  system?: string;
  outcome?: ValidationOutcome | '';
  days?: number;
  limit?: number;
}

/** One switchable top-level skill of a scope; `state` null = inherits `inherited`. */
export interface SkillToggle {
  key: string;
  name: string;
  description: string;
  origin: SkillOrigin;
  inherited: boolean;
  state: boolean | null;
}

// ------------------------------------------------------------------------------------------------ docs

export type DocSourceKind = 'confluence' | 'configtool';

/** Configuration Tool sources only: the AUTH sign-in of the service user and the language read. */
export interface DocSourceSettings {
  authUrl?: string;
  tenantId?: string;
  language?: string;
  /** `{id}` = page id; empty = hits carry no URL. */
  linkTemplate?: string;
}

export interface DocExternalShare {
  enabled: boolean;
  baseUrl?: string;
  hasApiKey?: boolean;
  /** Write-only; empty keeps the stored key. */
  apiKey?: string;
  spaceKeys: string[];
  shareUuids: string[];
}

export interface DocCrawlResult {
  pages?: number;
  skipped?: number;
  chunks?: number;
  errors?: string[];
  durationMs?: number;
  sharesUpdated?: number;
  error?: string;
}

export interface DocSource {
  id: string;
  kind: DocSourceKind | (string & {});
  name: string;
  baseUrl: string;
  email?: string;
  hasApiToken?: boolean;
  spaceKeys: string[];
  includeBlogPosts: boolean;
  indexPdfs: boolean;
  /** 0 = manual crawls only. */
  refreshMinutes: number;
  externalShare?: DocExternalShare;
  settings?: DocSourceSettings;
  enabled: boolean;
  lastCrawlAt?: string | null;
  lastResult?: DocCrawlResult | null;
  pages?: number;
  chunks?: number;
  pagesWithExternalUrl?: number;
}

/** buddy3 generates the id; `apiToken` and `externalShare.apiKey` are write-only (left out = keep the stored one). */
export interface DocSourceInput {
  kind: DocSourceKind;
  name: string;
  baseUrl: string;
  email: string;
  apiToken?: string;
  spaceKeys: string[];
  includeBlogPosts: boolean;
  indexPdfs: boolean;
  refreshMinutes: number;
  externalShare?: Omit<DocExternalShare, 'hasApiKey'>;
  settings?: DocSourceSettings;
  enabled: boolean;
}

export interface DocsStatus {
  available: boolean;
  /** pgvector installed. */
  extension: boolean;
  embeddingModel?: string;
  kinds?: string[];
  sources: DocSource[];
  totals: { pages: number; chunks: number };
  running?: { sourceId: string; startedAt: string; pages?: number } | null;
  error?: string;
}

/** `spaces` = the source's scopes: Confluence spaces or Configuration Tool function groups. */
export interface DocSourceTest {
  ok: boolean;
  site?: string;
  spaces?: { key: string; name: string; pages?: number }[];
  error?: string;
}

export interface ShareSyncResult {
  updated: number;
  cleared: number;
  errors: string[];
}

export interface DocSearchHit {
  title: string;
  source?: string;
  kind?: string;
  /** Configuration Tool pages: `function` or `strategy`. */
  type?: string;
  /** Absent when the page is named, not linked. */
  url?: string;
  excerpt: string;
  score?: number;
}

export interface DocSearchQuery {
  q: string;
  topK?: number;
  links?: 'internal' | 'external';
  /** Comma-separated corpora; empty = all. */
  kinds?: string;
  /** `function` | `strategy` (what search_config_tool's `type` becomes). */
  label?: string;
  /** Search as this system would: its own and its organization's customer documents too. */
  systemId?: string;
}

export interface DocSearchResult {
  hits: DocSearchHit[];
  method?: string;
  /** The exact bounded reply the model would see. */
  text?: string;
}

// ------------------------------------------------------------------------------------------------ customer documents

export type CustomerDocumentStatus = 'pending' | 'indexing' | 'ready' | 'failed';

export interface CustomerDocument {
  id: string;
  organizationId: string | null;
  systemId: string | null;
  title: string;
  fileName: string;
  mimeType: string;
  format: 'pdf' | 'docx' | 'markdown' | 'text' | 'html' | (string & {});
  size: number;
  status: CustomerDocumentStatus;
  error: string | null;
  chunks: number;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
  indexedAt: string | null;
}

/** Exactly one owner. */
export type DocOwner =
  { organizationId: string; systemId?: undefined } | { systemId: string; organizationId?: undefined };

/** buddy3's limits (`CustomerDocEndpoints`): it rejects the whole upload when one is broken. */
export const CUSTOMER_DOC_LIMITS = {
  files: 100,
  fileBytes: 20 * 1024 * 1024,
  totalBytes: 200 * 1024 * 1024,
  extensions: ['.pdf', '.docx', '.md', '.markdown', '.txt', '.html', '.htm'],
} as const;

/** What is wrong with a selection before it is sent, one line per problem; empty = fine. */
export function uploadProblems(files: readonly { name: string; size: number }[]): string[] {
  const L = CUSTOMER_DOC_LIMITS;
  const mb = (n: number) => `${Math.round(n / (1024 * 1024))} MB`;
  const p: string[] = [];
  if (files.length > L.files) p.push(`${files.length} files: at most ${L.files} per upload.`);
  for (const f of files) {
    const ext = f.name.includes('.') ? f.name.slice(f.name.lastIndexOf('.')).toLowerCase() : '';
    if (!(L.extensions as readonly string[]).includes(ext))
      p.push(`${f.name}: not a supported document (PDF, Word, Markdown, text or HTML).`);
    else if (f.size > L.fileBytes) p.push(`${f.name}: larger than ${mb(L.fileBytes)}.`);
    else if (f.size === 0) p.push(`${f.name}: empty.`);
  }
  const total = files.reduce((s, f) => s + f.size, 0);
  if (total > L.totalBytes) p.push(`${mb(total)} in all: at most ${mb(L.totalBytes)} per upload.`);
  return p;
}

// ------------------------------------------------------------------------------------------------ lookups

export interface NamedRef {
  id: string;
  name: string;
  organizationId?: string | null;
  organizationName?: string;
  tenantId?: string;
}

// ------------------------------------------------------------------------------------------------ client

const enc = encodeURIComponent;

/** The knowledge routes of one environment's nyce.buddy. */
export const knowledgeFor = (target: string) => {
  const { get, send, download, upload } = buddyClient(target);
  return {
    skills: {
      list: () => get<Skill[]>('/skills'),
      validators: () => get<SkillValidator[]>('/skills/validators'),
      validations: (q: ValidationQuery = {}) => get<Validations>(`/skills/validations${qs({ ...q })}`),
      effective: (systemId: string) => get<EffectiveSkills>(`/skills/effective/${enc(systemId)}`),
      create: (body: SkillInput) => send<Skill>('POST', '/skills', body),
      update: (id: string, body: SkillInput) => send<Skill>('PUT', `/skills/${enc(id)}`, body),
      remove: (id: string) => send<void>('DELETE', `/skills/${enc(id)}`),
      toggles: (scope: ToggleScope, scopeId: string) =>
        get<SkillToggle[]>(`/skills/toggles/${scope}/${enc(scopeId)}`),
      /** `{key: true | false | null}`; null = back to inherit. */
      setToggles: (scope: ToggleScope, scopeId: string, skills: Record<string, boolean | null>) =>
        send<{ changed: string[] }>('PUT', `/skills/toggles/${scope}/${enc(scopeId)}`, { skills }),
    },
    docs: {
      status: () => get<DocsStatus>('/docs/status'),
      create: (body: DocSourceInput) => send<DocSource>('POST', '/docs/sources', body),
      update: (id: string, body: DocSourceInput) => send<DocSource>('PUT', `/docs/sources/${enc(id)}`, body),
      remove: (id: string) => send<void>('DELETE', `/docs/sources/${enc(id)}`),
      crawl: (id: string, full: boolean) =>
        send<{ started: boolean }>('POST', `/docs/sources/${enc(id)}/crawl`, { full }),
      syncShares: (id: string) =>
        send<ShareSyncResult>('POST', `/docs/sources/${enc(id)}/external-share/sync`),
      /** A saved source, optionally with unsaved edits on top; without an id the unsaved form is tested. */
      test: (id: string | null, body?: DocSourceInput) =>
        send<DocSourceTest>('POST', id ? `/docs/sources/${enc(id)}/test` : '/docs/sources/test', body),
      reembed: () => send<{ started: boolean }>('POST', '/docs/reembed'),
      search: (q: DocSearchQuery) => get<DocSearchResult>(`/docs/search${qs({ ...q })}`),
    },
    customerDocs: {
      /** One owner's documents; without an owner every customer document of the environment. */
      list: (owner?: DocOwner) => get<CustomerDocument[]>(`/customer-docs${qs({ ...owner })}`),
      /** Multipart `file` × n, `title` for a single file, and the owner. */
      upload: (
        owner: DocOwner,
        files: readonly File[],
        title?: string,
        onProgress?: (sent: number, total: number) => void
      ) => {
        const form = new FormData();
        for (const f of files) form.append('file', f, f.name);
        if (title?.trim() && files.length === 1) form.append('title', title.trim());
        if (owner.organizationId) form.append('organizationId', owner.organizationId);
        else if (owner.systemId) form.append('systemId', owner.systemId);
        return upload<CustomerDocument[]>('/customer-docs', form, onProgress);
      },
      download: (d: Pick<CustomerDocument, 'id' | 'fileName'>) =>
        download(`/customer-docs/${enc(d.id)}/content`, d.fileName),
      reindex: (id: string) => send<CustomerDocument>('POST', `/customer-docs/${enc(id)}/reindex`),
      remove: (id: string) => send<void>('DELETE', `/customer-docs/${enc(id)}`),
    },
    lookups: {
      systems: () => get<NamedRef[]>('/systems'),
      organizations: () => get<NamedRef[]>('/organizations'),
      tenants: () => get<NamedRef[]>('/tenants'),
    },
  };
};
